/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { createSelector } from "@reduxjs/toolkit";
import { encode as base64Encode } from "base-64";
import { AppState } from "../..";
import { IfilterObj } from "../types";
import { selectedGeoRegionsSelector } from "../../api/admin/selectors";
import { deviceRegionFilterIdsSelector } from "../Nodes/selectors";
import { selectedNavListItemSelector } from "../../helpers/helperSelectors";
import dayjs from "dayjs";

export const reportFilterSelector = (state: AppState) => state.reportFilter;
export const selectedReportTypeSelector = (state: AppState) => state.reportFilter?.selectedReportType;
export const selectedReportTaskSelector = (state: AppState) => state.reportFilter?.selectedReportTask;
export const selectedReportCreatedSelector = (state: AppState) => state.reportFilter?.selectedReportCreated;

export const urlParameterReportSelector = createSelector(
  selectedGeoRegionsSelector,
  selectedReportTypeSelector,
  selectedReportTaskSelector,
  selectedReportCreatedSelector,
  deviceRegionFilterIdsSelector,
  selectedNavListItemSelector,
  (geoRegions, reportType, reportTask, reportCreated, navRegionFilterIds, selectedNavItem) => {
    const params = new URLSearchParams();
    const isUngrouped = selectedNavItem === "ungrouped";
    const hasNavRegionFilter = !isUngrouped && !!(navRegionFilterIds && navRegionFilterIds.length > 0);
    const hasRegions = !isUngrouped && Array.isArray(geoRegions) && geoRegions.length > 0;
    const isEnableFilter = hasNavRegionFilter || hasRegions || !!reportType || !!reportTask || !!reportCreated;
    if (isEnableFilter) {
      const paramsString: IfilterObj = { type: "AND", operator: "", fieldName: "", value: "", predicates: [] };

      // Nav panel region selection takes highest priority
      if (hasNavRegionFilter) {
        const navRegionNames = geoRegions
          .filter((r: any) => navRegionFilterIds.includes(r.objectKey))
          .map((r: any) => r?.name)
          .filter((name: string | undefined) => !!name) as string[];

        if (navRegionNames.length === 1) {
          paramsString.predicates.push({ type: "", operator: "EQ", fieldName: "geoRegion", value: navRegionNames[0], predicates: [{}] });
        } else if (navRegionNames.length > 1) {
          paramsString.predicates.push({ type: "", operator: "IN", fieldName: "geoRegion", value: navRegionNames.join(","), predicates: [{}] });
        }
      } else if (hasRegions) {
        const regionNames = geoRegions
          .map((r: any) => r?.name)
          .filter((name: string | undefined) => !!name) as string[];

        if (regionNames.length === 1) {
          paramsString.predicates.push({ type: "", operator: "EQ", fieldName: "geoRegion", value: regionNames[0], predicates: [{}] });
        } else if (regionNames.length > 1) {
          paramsString.predicates.push({ type: "", operator: "IN", fieldName: "geoRegion", value: regionNames.join(","), predicates: [{}] });
        }
      }
      if (reportType) {
        paramsString.predicates.push({ type: "", operator: "EQ", fieldName: "type", value: reportType, predicates: [{}] });
      }
      if (reportTask) {
        paramsString.predicates.push({ type: "", operator: "EQ", fieldName: "taskName", value: reportTask, predicates: [{}] });
      }
      if (reportCreated) {
        const createdInMiliSec = dayjs(reportCreated).valueOf();
        paramsString.predicates.push({ type: "", operator: "LTE", fieldName: "createdTS", value: createdInMiliSec.toString(), predicates: [{}] });
      }
      const base64EncodedStr = base64Encode(JSON.stringify(paramsString));
      if (paramsString?.predicates.length > 0) {
        base64EncodedStr && params.append("filters", base64EncodedStr);
      }
      //console.log("Report Filter", decode(base64EncodedStr));
    }
    if (isUngrouped) {
      params.append("ungrouped", "true");
    }
    return params;
  }
);
